import React, { useState, useMemo } from 'react';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { useUserTeamsStore } from '@/stores/userTeamsStore';
import { useServiceHealthStore } from '@/stores/serviceHealthStore';
import { Search, Plus, Package, ExternalLink, GitBranch, FileText, CheckCircle, AlertTriangle, Clock, Zap, Trash2, Lock, Info, Star, Users, Activity, XCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import type { Service } from '@/types/service';
import { ServiceType, ServiceHealthStatus } from '@/types/api';
import { useReadOnlyMode } from '@nimbitlabs/features';
import { PermissionGate } from '@nimbitlabs/features';
import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@nimbitlabs/ui';

export const Services: React.FC = () => {
  const navigate = useNavigate();
  const { services, loading, deleteService: deleteServiceFn } = useServicesStore();
  const { teams: allTeams } = useTeamsStore();
  const { isReadOnly } = useReadOnlyMode();
  const { userTeamIds } = useUserTeamsStore();
  const { healthData, getHealthByService } = useServiceHealthStore();
  
  // Local filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [showMyTeamsOnly, setShowMyTeamsOnly] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const toggleFavorite = (serviceId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(serviceId)) {
        newFavorites.delete(serviceId);
      } else {
        newFavorites.add(serviceId);
      }
      return newFavorites;
    });
  };
  
  // Delete service handler
  const deleteService = async (id: string) => {
    await deleteServiceFn(id);
  };

  // Get the production environment health status for a service
  const getServiceHealthStatus = (serviceId: string): ServiceHealthStatus => {
    const healthChecks = getHealthByService(serviceId);
    
    // Filter for production environment only
    const productionHealthChecks = healthChecks.filter(h => 
      h.environmentName.toLowerCase().includes('prod')
    );
    
    if (productionHealthChecks.length === 0) {
      return ServiceHealthStatus.Unknown;
    }
    
    // If any are unhealthy/timeout/dnsfailure, show unhealthy
    if (productionHealthChecks.some(h => 
      h.status === ServiceHealthStatus.Unhealthy || 
      h.status === ServiceHealthStatus.Timeout || 
      h.status === ServiceHealthStatus.DnsFailure
    )) {
      return ServiceHealthStatus.Unhealthy;
    }
    
    // If any are degraded, show degraded
    if (productionHealthChecks.some(h => h.status === ServiceHealthStatus.Degraded)) {
      return ServiceHealthStatus.Degraded;
    }
    
    // If all are healthy, show healthy
    if (productionHealthChecks.every(h => h.status === ServiceHealthStatus.Healthy)) {
      return ServiceHealthStatus.Healthy;
    }
    
    // Otherwise unknown
    return ServiceHealthStatus.Unknown;
  };

  const getStatusIcon = (status: string, isHealthStatus: boolean = false) => {
    const normalizedStatus = status.toLowerCase();
    
    if (isHealthStatus) {
      switch (normalizedStatus) {
        case 'healthy':
          return <CheckCircle className="h-4 w-4" />;
        case 'degraded':
          return <AlertTriangle className="h-4 w-4" />;
        case 'unhealthy':
        case 'timeout':
        case 'dnsfailure':
          return <XCircle className="h-4 w-4" />;
        default:
          return <Activity className="h-4 w-4" />;
      }
    }
    
    // Operational status icons
    switch (normalizedStatus) {
      case 'available':
      case 'healthy':
        return <CheckCircle className="h-4 w-4" />;
      case 'down':
      case 'error':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string, isHealthStatus: boolean = false) => {
    const normalizedStatus = status.toLowerCase();
    
    if (isHealthStatus) {
      switch (normalizedStatus) {
        case 'healthy':
          return 'bg-green-500/20 text-green-600 border-green-500/40 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40';
        case 'degraded':
          return 'bg-yellow-500/20 text-yellow-600 border-yellow-500/40 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-500/40';
        case 'unhealthy':
        case 'timeout':
        case 'dnsfailure':
          return 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400';
        default:
          return 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400';
      }
    }
    
    // Operational status colors
    switch (normalizedStatus) {
      case 'available':
      case 'healthy':
        return 'bg-green-500/20 text-green-600 border-green-500/40 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40';
      case 'down':
      case 'error':
        return 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400';
      default:
        return 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case ServiceType.Frontend:
        return '🌐';
      case ServiceType.Api:
        return '⚡';
      case ServiceType.Database:
        return '🗄️';
      case ServiceType.Microservice:
        return '🔧';
      default:
        return '📦';
    }
  };

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Service Catalog</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Manage and monitor all your services in one place
          </p>
        </div>
        <PermissionGate permission="services:create">
          <Button 
            onClick={() => navigate('/services/new')} 
            className="w-fit"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Service
          </Button>
        </PermissionGate>
      </div>

      {/* Locked Resources Alert */}
      {services.some(s => s.isLocked) && (
        <Alert className="border border-warning/15 bg-warning/5">
          <Info className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning">Some services are locked</AlertTitle>
          <AlertDescription>
            {services.filter(s => s.isLocked).length} service(s) are locked due to subscription limits. 
            Your data is safe. Delete services to free up quota or upgrade your plan to unlock them.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="my-teams-only" 
                  checked={showMyTeamsOnly}
                  onCheckedChange={(checked) => setShowMyTeamsOnly(checked === true)}
                />
                <label 
                  htmlFor="my-teams-only" 
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Show only services from my teams
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="favorites-only" 
                  checked={showFavoritesOnly}
                  onCheckedChange={(checked) => setShowFavoritesOnly(checked === true)}
                />
                <label 
                  htmlFor="favorites-only" 
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1"
                >
                  <Star className="h-3.5 w-3.5 text-warning fill-warning" />
                  Show only favorites
                </label>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input 
                  placeholder="Search services..." 
                  value={searchQuery} 
                  onChange={e => setSearchQuery(e.target.value)} 
                  className="pl-10" 
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="down">Down</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value={ServiceType.Frontend}>Frontend</SelectItem>
                  <SelectItem value={ServiceType.Api}>API</SelectItem>
                  <SelectItem value={ServiceType.Database}>Database</SelectItem>
                  <SelectItem value={ServiceType.Microservice}>Microservice</SelectItem>
                </SelectContent>
              </Select>
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {allTeams.map(team => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services Grid */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-4 bg-muted rounded" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services
              .filter(service => {
                const q = searchQuery.toLowerCase();
                const matchesSearch =
                  service.name.toLowerCase().includes(q) ||
                  service.description.toLowerCase().includes(q) ||
                  (service.tags ?? []).some(tag => tag.toLowerCase().includes(q));
                const matchesStatus = statusFilter === 'all' || service.status === statusFilter;
                const matchesType = typeFilter === 'all' || service.type === typeFilter;
                const matchesTeam = teamFilter === 'all' || service.teamId === teamFilter;
                return matchesSearch && matchesStatus && matchesType && matchesTeam;
              })
              .filter(service => !showMyTeamsOnly || userTeamIds.includes(service.teamId))
              .filter(service => !showFavoritesOnly || favorites.has(service.id))
              .sort((a, b) => {
                // Favorites first
                const aFav = favorites.has(a.id) ? 0 : 1;
                const bFav = favorites.has(b.id) ? 0 : 1;
                if (aFav !== bFav) return aFav - bFav;
                
                // Then unlocked before locked
                if (a.isLocked === b.isLocked) return 0;
                return a.isLocked ? 1 : -1;
              })
              .map((service, index) => (
                <Card
                  key={service.id}
                  className={`group relative overflow-hidden border-border/40 bg-card/50 hover:shadow-lg hover:border-primary/30 hover:bg-card transition-all duration-300 animate-fade-in ${
                    service.isLocked ? 'opacity-60' : 'cursor-pointer'
                  } ${favorites.has(service.id) ? 'ring-2 ring-primary/20' : ''}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => !service.isLocked && navigate(`/services/${service.id}`)}
                >
                  {/* Favorite Star */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`absolute top-3 right-3 z-10 h-8 w-8 p-0 hover:bg-primary/10 transition-all ${
                      favorites.has(service.id) ? 'text-warning' : 'text-muted-foreground'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(service.id);
                    }}
                  >
                    <Star className={`h-4 w-4 ${favorites.has(service.id) ? 'fill-warning' : ''}`} />
                  </Button>

                  <CardHeader className="pb-4 pr-12 h-[140px]">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-2xl">
                        {getTypeIcon(service.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <CardTitle className="text-xl font-bold truncate">{service.name}</CardTitle>
                          
                          {/* Status Badge - Production Health Check Status */}
                          {(() => {
                            const healthStatus = getServiceHealthStatus(service.id);
                            
                            return (
                              <Badge 
                                variant="outline" 
                                className={`flex items-center gap-1 ${getStatusColor(healthStatus, true)}`}
                              >
                                {getStatusIcon(healthStatus, true)}
                                <span className="capitalize">{healthStatus}</span>
                              </Badge>
                            );
                          })()}

                          {service.isLocked && (
                            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 flex-shrink-0">
                              <Lock className="h-3 w-3 mr-1" />
                              Locked
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="line-clamp-2 text-sm min-h-[40px]">
                          {service.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex flex-col pt-0 h-[280px]">
                    {/* Team Badge */}
                    <div className="flex items-center justify-between py-3 border-y border-border/40 mb-4">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Owned by
                        </span>
                      </div>
                      <Link 
                        to={`/teams?search=${service.team}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Badge 
                          variant="secondary" 
                          className="bg-gradient-to-r from-blue-500/15 to-cyan-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 hover:from-blue-500/25 hover:to-cyan-500/25 hover:border-blue-500/40 transition-all font-semibold px-3 py-1"
                        >
                          {service.team}
                        </Badge>
                      </Link>
                    </div>

                    {/* Tags */}
                    <div className="flex-1 mb-4">
                      {service.tags.length > 0 && (
                      <div className="space-y-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Tags
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {service.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="text-xs bg-cyan-500/10 text-cyan-600 border-cyan-500/30 hover:bg-cyan-500/20 transition-colors cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery(tag);
                              }}
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Actions - Always at bottom */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1.5">
                        <PermissionGate permission="services:delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (confirm('Are you sure you want to delete this service?')) {
                                try {
                                  await deleteService(service.id);
                                } catch (error) {
                                  // Error handled by store
                                }
                              }
                            }}
                            className="h-9 w-9 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-all group/btn"
                            title="Delete service"
                          >
                            <Trash2 className="h-4 w-4 group-hover/btn:rotate-12 transition-transform" />
                          </Button>
                        </PermissionGate>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={service.isLocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!service.isLocked) {
                            navigate(`/services/${service.id}`);
                          }
                        }}
                        className="font-medium bg-blue-500/10 text-blue-600 border-blue-500/30 hover:bg-blue-500/20 hover:border-blue-500/40 transition-all"
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>

          {!loading && services.filter(service => !showMyTeamsOnly || userTeamIds.includes(service.teamId)).length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No services found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || teamFilter !== 'all' 
                    ? 'Try adjusting your search criteria.' 
                    : 'Get started by creating your first service using the button above.'}
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
